import { supabase } from '@/integrations/supabase/client';
import { getDiasUteisMes } from '@/utils/diasUteis';
import { convertHoursToDecimal } from './timeConversion';

export interface PostImportProcessingResult {
  processedDays: number;
  processedEmployees: number;
  updatedOrders: number;
  errors: string[];
}

interface OSRow {
  numero: string;
  operador_nome: string;
  data_real: string;
  horas_totais: string | null;
  status_descricao: string | null;
  projeto_fechado: boolean | null;
  valor_a_pagar: number | null;
}

const PAGE_SIZE = 1000;
const RPC_CHUNK = 3000;

function isRpcMissing(err: { message?: string } | null): boolean {
  if (!err?.message) return false;
  const m = err.message.toLowerCase();
  return (
    (m.includes('function') || m.includes('função')) &&
    (m.includes('does not exist') || m.includes('not found') || m.includes('não existe'))
  );
}

function getValorHoraHistorico(historico: any[] | null | undefined, dataOS: Date): number {
  if (!historico?.length) return 0;
  const tLim = dataOS.getTime();
  let bestT = -Infinity;
  let bestV = 0;
  for (const h of historico) {
    const t = new Date(h.data_reajuste).getTime();
    if (t <= tLim && t >= bestT) {
      bestT = t;
      bestV = Number(h.valor_hora) || 0;
    }
  }
  return bestV;
}

async function fetchAllOSForPeriod(
  dataInicio?: string,
  dataFim?: string
): Promise<OSRow[]> {
  const all: OSRow[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    let q = supabase
      .from('ordens_servico')
      .select('numero, operador_nome, data_real, horas_totais, status_descricao, projeto_fechado, valor_a_pagar')
      .not('operador_nome', 'is', null)
      .not('data_real', 'is', null)
      .order('data_real', { ascending: true })
      .order('numero', { ascending: true });

    if (dataInicio) q = q.gte('data_real', dataInicio);
    if (dataFim) q = q.lte('data_real', dataFim);

    const { data, error } = await q.range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    if (!data?.length) break;
    all.push(...(data as OSRow[]));
    if (data.length < PAGE_SIZE) break;
  }
  return all;
}

async function bulkUpdateValorAPagar(
  updates: { numero: string; valor_a_pagar: number }[]
): Promise<{ updated: number; errors: number }> {
  if (updates.length === 0) return { updated: 0, errors: 0 };

  let totalUpdated = 0;
  let totalErrors = 0;

  for (let i = 0; i < updates.length; i += RPC_CHUNK) {
    const chunk = updates.slice(i, i + RPC_CHUNK);
    const { data, error } = await supabase.rpc(
      'bulk_update_ordens_servico_valor_a_pagar',
      { p_updates: chunk }
    );

    if (!error) {
      totalUpdated += typeof data === 'number' ? data : chunk.length;
      continue;
    }

    if (isRpcMissing(error)) {
      for (let j = 0; j < chunk.length; j += 64) {
        const slice = chunk.slice(j, j + 64);
        const outcomes = await Promise.all(
          slice.map(async (u) => {
            const { error: e } = await supabase
              .from('ordens_servico')
              .update({ valor_a_pagar: u.valor_a_pagar })
              .eq('numero', u.numero);
            return !!e;
          })
        );
        for (const hadErr of outcomes) {
          if (hadErr) totalErrors++;
          else totalUpdated++;
        }
      }
    } else {
      totalErrors += chunk.length;
    }
  }

  return { updated: totalUpdated, errors: totalErrors };
}

export const processPostImportExtraCosts = async (
  dataInicio?: string,
  dataFim?: string
): Promise<PostImportProcessingResult> => {
  const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();

  const result: PostImportProcessingResult = {
    processedDays: 0,
    processedEmployees: 0,
    updatedOrders: 0,
    errors: [],
  };

  try {
    const [funcionariosRes, allOS] = await Promise.all([
      supabase
        .from('funcionarios')
        .select(`
          nome, regime, ajuda_custo, vale_alimentacao, plano_saude,
          valores_historico:funcionarios_valores_historico(data_reajuste, valor_hora)
        `)
        .eq('ativo', true),
      fetchAllOSForPeriod(dataInicio, dataFim),
    ]);

    if (funcionariosRes.error) {
      result.errors.push(`Erro ao buscar funcionários: ${funcionariosRes.error.message}`);
      return result;
    }

    const funcionarios = funcionariosRes.data || [];
    if (funcionarios.length === 0 || allOS.length === 0) return result;

    const funcionarioMap = new Map<string, (typeof funcionarios)[0]>();
    for (const f of funcionarios) {
      funcionarioMap.set(f.nome.trim().toLowerCase(), f);
    }

    // Pre-compute: distinct days worked per operator per month
    const operadorMesDias = new Map<string, Set<string>>();
    for (const os of allOS) {
      const opNorm = os.operador_nome.trim().toLowerCase();
      const dataStr = os.data_real.slice(0, 10);
      const mesYM = dataStr.slice(0, 7); // YYYY-MM
      const mesKey = `${opNorm}|${mesYM}`;
      if (!operadorMesDias.has(mesKey)) operadorMesDias.set(mesKey, new Set());
      operadorMesDias.get(mesKey)!.add(dataStr);
    }

    // Pre-compute: OS count per operator per day (non-cancelled only)
    const operadorDiaOsCount = new Map<string, number>();
    for (const os of allOS) {
      const statusLower = (os.status_descricao || '').toLowerCase();
      if (statusLower === 'cancelada') continue;
      const opNorm = os.operador_nome.trim().toLowerCase();
      const dataStr = os.data_real.slice(0, 10);
      const diaKey = `${opNorm}|${dataStr}`;
      operadorDiaOsCount.set(diaKey, (operadorDiaOsCount.get(diaKey) || 0) + 1);
    }

    const pendingUpdates: { numero: string; valor_a_pagar: number }[] = [];
    const processedOperadorDias = new Set<string>();

    for (const os of allOS) {
      const statusLower = (os.status_descricao || '').toLowerCase();
      if (statusLower === 'cancelada' || os.projeto_fechado) continue;

      const opNorm = os.operador_nome.trim().toLowerCase();
      const funcionario = funcionarioMap.get(opNorm);
      if (!funcionario) continue;

      const dataStr = os.data_real.slice(0, 10);
      const dataOS = new Date(dataStr);
      const ano = dataOS.getFullYear();
      const mes = dataOS.getMonth() + 1;
      const mesYM = dataStr.slice(0, 7);
      const mesKey = `${opNorm}|${mesYM}`;
      const diaKey = `${opNorm}|${dataStr}`;

      const diasUteis = getDiasUteisMes(ano, mes);
      if (diasUteis <= 0) continue;

      const diasTrabalhados = operadorMesDias.get(mesKey)?.size || 1;

      let custoExtraDiario = 0;
      if (funcionario.regime === 'PJ') {
        const diasParaCalculo = Math.max(diasUteis, diasTrabalhados);
        custoExtraDiario = (funcionario.ajuda_custo || 0) / diasParaCalculo;
      } else if (funcionario.regime === 'CLT') {
        const valeAlimentacaoTotal = (funcionario.vale_alimentacao || 0) * diasTrabalhados;
        const planoSaudeMensal = funcionario.plano_saude || 0;
        const custoTotal = valeAlimentacaoTotal + planoSaudeMensal;
        custoExtraDiario = custoTotal / diasTrabalhados;
      }

      if (custoExtraDiario === 0) continue;

      const custoExtraPorOs = custoExtraDiario;

      const valorHoraFuncionario = getValorHoraHistorico(funcionario.valores_historico, dataOS);
      const horasTotais = convertHoursToDecimal(os.horas_totais || '0');
      const custoBase = valorHoraFuncionario * horasTotais;
      const novoValorAPagar = Math.round((custoBase + custoExtraPorOs) * 100) / 100;

      if (os.valor_a_pagar != null && Math.abs(Number(os.valor_a_pagar) - novoValorAPagar) <= 0.01) {
        continue;
      }

      pendingUpdates.push({ numero: os.numero, valor_a_pagar: novoValorAPagar });

      if (!processedOperadorDias.has(diaKey)) {
        processedOperadorDias.add(diaKey);
        result.processedDays++;
      }
    }

    const uniqueOperators = new Set<string>();
    for (const key of processedOperadorDias) {
      uniqueOperators.add(key.split('|')[0]);
    }
    result.processedEmployees = uniqueOperators.size;

    if (pendingUpdates.length > 0) {
      const dbResult = await bulkUpdateValorAPagar(pendingUpdates);
      result.updatedOrders = dbResult.updated;
      if (dbResult.errors > 0) {
        result.errors.push(`${dbResult.errors} erros durante bulk update`);
      }
    }

    const elapsed = typeof performance !== 'undefined'
      ? Math.round(performance.now() - t0)
      : Math.round(Date.now() - t0);
    console.log(
      `[post-import] concluído: ${allOS.length} OS, ${pendingUpdates.length} atualizadas, ${result.processedDays} dias, ~${elapsed}ms`
    );

    return result;
  } catch (error: any) {
    result.errors.push(`Erro geral no processamento: ${error.message}`);
    return result;
  }
};

export const processExtraCostsForPeriod = async (
  dataInicio: string,
  dataFim: string,
  _operadorNome?: string
): Promise<PostImportProcessingResult> => {
  return processPostImportExtraCosts(dataInicio, dataFim);
};
